import React, { useEffect, useState } from 'react';
import { 
  Button, Box, Container, Paper, Typography, CircularProgress, Backdrop,
  TextField, InputAdornment, Tooltip, IconButton, Stack, Chip, Card, CardContent, Grid,
  Menu, MenuItem, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import * as saleOrderService from '../services/saleOrderService';
import { generateAndPreviewSaleOrderPDF } from '../services/saleOrderPdfService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

// Icons
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Components
import SaleOrderStatusChip from '../components/SaleOrderStatusChip';
import PaymentStatusChip from '../components/PaymentStatusChip';

const ListSaleOrderPage = () => {
  const [saleOrders, setSaleOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSaleOrder, setSelectedSaleOrder] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSaleOrder, setMenuSaleOrder] = useState(null);
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const language = useSelector((state) => state.settings?.language) || 'en';
  
  const { 
    page, 
    pageSize, 
    pageSizeOptions,
    handlePageChange, 
    handlePageSizeChange 
  } = usePaginationWithUrl({ defaultPageSize: 10, pageSizeOptions: [10, 20, 50, 100] });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchSaleOrders();
  }, [page, pageSize, searchQuery]);

  const fetchSaleOrders = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: page + 1,
        pageSize: pageSize,
        searchTerm: searchQuery || undefined,
        sortBy: 'SaleOrderDate',
        sortOrder: 'desc',
      };
      
      const response = await saleOrderService.getSaleOrders(params);
      // Handle paginated response structure
      const data = response?.payload || response?.data || response;
      
      console.log('Full API response:', response);
      console.log('Parsed data:', data);
      
      const saleOrdersArray = data?.Items || data?.items || data || [];
      const total = data?.TotalItems || data?.TotalCount || data?.totalItems || data?.totalCount || saleOrdersArray.length;
      
      console.log('Sale orders array:', saleOrdersArray);
      console.log('Total count:', total);
      console.log('Total pages:', data?.TotalPages);
      console.log('Has next page:', data?.HasNextPage);
      console.log('Current page:', data?.CurrentPage || data?.PageNumber);
      
      setSaleOrders(Array.isArray(saleOrdersArray) ? saleOrdersArray : []);
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching sale orders:', error);
      toast.error(t('failed_to_load_sale_orders'));
      setSaleOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSaleOrders();
      toast.success(t('sale_orders_refreshed'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteClick = (saleOrder) => {
    setSelectedSaleOrder(saleOrder);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event, saleOrder) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuSaleOrder(saleOrder);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSaleOrder(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSaleOrder) return;
    
    try {
      await saleOrderService.deleteSaleOrder(selectedSaleOrder.Id);
      toast.success(t('sale_order_deleted'));
      fetchSaleOrders();
    } catch (error) {
      console.error('Error deleting sale order:', error);
      toast.error(t('failed_to_delete_sale_order'));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSaleOrder(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const handlePreviewPdf = async (saleOrder) => {
    setPreviewLoading(true);
    try {
      const fullSO = await saleOrderService.getSaleOrderById(saleOrder.Id);
      const data = fullSO?.payload || fullSO?.data || fullSO || saleOrder;
      
      await generateAndPreviewSaleOrderPDF(data, storeInfo, language, t);
      
      toast.success(t('pdf_generated'));
    } catch (error) {
      console.error('Error generating sale order PDF:', error);
      toast.error(t('failed_to_generate_pdf'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Stats calculation - current page only
  const stats = {
    total: totalCount,
    draftOnPage: saleOrders.filter(so => so.Status?.toLowerCase() === 'draft').length,
    closedOnPage: saleOrders.filter(so => so.Status?.toLowerCase() === 'closed').length,
    totalValueOnPage: saleOrders.reduce((sum, so) => sum + (Number(so.TotalIncl) || 0), 0),
  };

  const isDraft = menuSaleOrder?.Status?.toLowerCase() === 'draft';
  const isClosed = menuSaleOrder?.Status?.toLowerCase() === 'closed';
  const isAlreadyConverted = Boolean(menuSaleOrder?.InvoiceId || menuSaleOrder?.InvoiceNumber);
  const canConvertToInvoice = isClosed && !isAlreadyConverted;
  
  // Get current page data
  const displayedSaleOrders = saleOrders;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/sales/sale-orders/${menuSaleOrder?.Id}`);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('view_details')}</ListItemText>
        </MenuItem>

        {isDraft && (
          <MenuItem onClick={() => {
            navigate(`/sales/sale-orders/add?id=${menuSaleOrder?.Id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('continue')}</ListItemText>
          </MenuItem>
        )}

        {canConvertToInvoice && (
          <MenuItem onClick={() => {
            navigate(`/sales/sale-orders/convert?id=${menuSaleOrder?.Id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>{t('convert_to_invoice')}</ListItemText>
          </MenuItem>
        )}

        {isAlreadyConverted && (
          <MenuItem onClick={() => {
            navigate(`/invoices/view/${menuSaleOrder?.InvoiceId}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" sx={{ color: '#9c27b0' }} />
            </ListItemIcon>
            <ListItemText>{t('view_invoice')}: {menuSaleOrder?.InvoiceNumber}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          menuSaleOrder && handlePreviewPdf(menuSaleOrder);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('preview_pdf')}</ListItemText>
        </MenuItem>

        {isDraft && (
          <MenuItem onClick={() => {
            menuSaleOrder && handleDeleteClick(menuSaleOrder);
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{t('delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t('sale_orders')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t('manage_sale_orders')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/sales/sale-orders/add')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              fontWeight: 700,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            {t('create_sale_order')}
          </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_sale_orders')}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('draft_orders')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {stats.draftOnPage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('closed_orders')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {stats.closedOnPage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_value')} ({t('current_page')})
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {formatCurrency(stats.totalValueOnPage)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('search_sale_orders_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={t('refresh')}>
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('sale_order_number')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('customer')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('date')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>
                      {t('payment_status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea', textAlign: 'right' }}>
                      {t('total')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea', textAlign: 'center' }}>
                      {t('actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSaleOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('no_sale_orders_found')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedSaleOrders.map((so) => (
                      <TableRow 
                        key={so.Id}
                        sx={{
                          '&:hover': { bgcolor: '#f8f9fa', cursor: 'pointer' },
                          borderBottom: '1px solid #e0e0e0'
                        }}
                        onClick={() => navigate(`/sales/sale-orders/${so.Id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShoppingCartIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={600}>
                              {so.SaleOrderNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {so.Customer?.CompanyName || so.Customer?.Name || so.CustomerName || t('unknown_customer')}
                        </TableCell>
                        <TableCell>
                          {formatDate(so.CreatedDate)}
                        </TableCell>
                        <TableCell>
                          <SaleOrderStatusChip status={so.Status} />
                        </TableCell>
                        <TableCell>
                          <PaymentStatusChip paymentStatus={so.PaymentStatus || PAYMENT_STATUS.NOT_PAID} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(so.TotalIncl)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, so);
                            }}
                            sx={{ color: '#666' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={pageSizeOptions}
              showFirstButton
              showLastButton
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '.MuiTablePagination-actions': {
                  display: 'flex'
                }
              }}
            />
          </>
        )}
      </Paper>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={previewLoading || refreshing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete')}
        message={t('confirm_delete_sale_order_message', { number: selectedSaleOrder?.SaleOrderNumber })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedSaleOrder(null);
        }}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />
    </Container>
  );
};

export default ListSaleOrderPage;
